import TableCol from "@rio-cloud/rio-uikit/TableCol";
import TableHead from "@rio-cloud/rio-uikit/TableHead";
import useSorting from "@rio-cloud/rio-uikit/useSorting";
import SortDirection from '@rio-cloud/rio-uikit/SortDirection';
import OverlayTrigger from '@rio-cloud/rio-uikit/components/overlay/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/components/tooltip/Tooltip';
import Tag from '@rio-cloud/rio-uikit/Tag';
import {
  datetimeFormatter,
  formatLatitudeLongitude,
  useTrackChanges,
} from "../../shared/utils";
import { Alert } from "../../shared/models/Alert";
import { Dialog } from "@rio-cloud/rio-uikit";
import MapEvents from "../../shared/components/Maps";
import { useState } from "react";
import styles from "./AlertsTable.module.css";
import { useGetAssetsQuery } from "../../services/assetsApi";

const columnLabels = new Map([
  ["vehicle", "Veículo"],
  ["chassis", "Chassi"],
  ["datetimeUtc", "Data e Hora"],
  ["location", "Localização"],
  ["alert", "Alerta"],
] as const);

type ColumnLabelEntries = typeof columnLabels extends Map<infer K, unknown>
  ? K
  : never;


interface AlertMap extends Alert {
  vehicle: string,
}

export function AlertsTable({alerts} : { alerts: Alert[] }) {

  const { currentData } = useGetAssetsQuery();
  
  const { isNew } = useTrackChanges(alerts??[], {
    getId: (alert) => alert.id,
    highlightDuration: 5000,
  });

  const { sortedItems, sortKey, sortDirection, setSortKey, toggleDirection } =
    useSorting(alerts??[], "datetimeUtc", SortDirection.DESCENDING);

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");
    if (newSortBy) {
      setSortKey(newSortBy as ColumnLabelEntries);
    }
    if (newSortBy === sortKey) {
      toggleDirection();
    }
  };

  const [showDialog, setShowDialog] = useState(false);
  const [alertMap, setAlertMap] = useState<AlertMap>();

  const handleOnClickMap = (alert: Alert, vehicleName: string) => {
    setShowDialog(true);
    setAlertMap({...alert, vehicle: vehicleName});
  }

  return (
    <div className="overflow-x-auto panel panel-default padding-0 panel-body margin-bottom-0">
      <Dialog
        show={showDialog}
        body={<MapEvents lat={alertMap?.location.latitude} lng={alertMap?.location.longitude} />}
        title={`Localização do Evento - ${alertMap?.vehicle}`}
        onClose={() => {
          setShowDialog(false);
          setAlertMap(undefined);
        }}
        showCloseButton
        bsSize='lg'
      />
      <table className="table table-layout-fixed table-column-overflow-hidden table-sticky-in-container table-head-filled">
        <colgroup>
          {[...columnLabels].map(([key]) => (
            <TableCol key={key} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {[...columnLabels].map(([key, label]) => (
              <TableHead
                key={key}
                label={label}
                column={key}
                sortBy={sortKey as typeof key}
                sortDir={sortDirection}
                onClick={handleSortChange}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((alert) => {
            const asset = currentData?.items.find(element=>element.id == alert.asset_id);
            return (
              <tr
                data-id={alert.id}
                className={isNew(alert) ? "animate-highlight" : ""}
              >
                <td className="white-space-nowrap">{asset ? asset.name : ''}</td>
                <td className="white-space-nowrap">{asset ? asset.identification : ''}</td>
                <td className="white-space-nowrap">
                  {datetimeFormatter.format(new Date(alert.datetimeUtc))}
                </td>
                <td className="white-space-nowrap">
                  <span className="rioglyph rioglyph-map-marker rioglyph-align-baseline text-size-16" />{" "}
                  {alert.location.latitude && alert.location.longitude ? (
                    <button type="button" style={{appearance: "none", font: "inherit", background: "transparent", border: "none"}} onClick={() => handleOnClickMap(alert, asset?.name?asset?.name:"")}>
                    {formatLatitudeLongitude(
                      alert.location.latitude,
                      alert.location.longitude
                    )}
                  </button>
                  ):(
                    <span>N/A</span>
                  )
                  }
                </td>
                <td className="white-space-nowrap">
                  <div className="display-flex flex-wrap gap-10">
                    <div
                      className={`label ${
                        alert.alert === "attention"
                          ? `label-secondary ${styles['label-gray']}`
                          : alert.alert === "instability"
                          ? `label-primary ${styles['label-gray']}`
                          : alert.alert === "operationalInsistence"
                          ? `label-default ${styles['label-gray']}`
                          : `label-muted ${styles['label-gray']}`
                      }`}
                    >
                      {InfractionMap.get(alert.alert) || alert.alert || "Desconhecido"}
                    </div>
                    {alert.alert === "operationalInsistence" && alert.details?.code && (
                      alert.details?.description?.trim() && alert.details.description !== "UNIDENTIFIED" ? (
                        <OverlayTrigger
                          overlay={
                            <Tooltip id={`tooltip-${alert.id}`} allowOnTouch>
                              {alert.details.description}
                            </Tooltip>
                          }
                          placement="top"
                        >
                          <Tag size="small">
                            Cód. {alert.details.code}{' '}
                            <span className="rioglyph rioglyph-info-sign margin-left-8 text-size-16" />
                          </Tag>
                        </OverlayTrigger>
                      ) : (
                        <Tag size="small">
                          Cód. {alert.details.code}
                        </Tag>
                      )
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      
    </div>
  );
}

const InfractionMap = new Map<Alert["alert"], string>([
  ["instability", "Condição de Instabilidade"],
  ["attention", "Condução Desatenta"],
  ["operationalInsistence", "Insistência Operacional"],
]);
